import { MoreOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { HighlightActionButton } from '../../../../components'
import {
  NOTIFICATION_DEFAULT_DURATION,
  algorithmRunResumeNodeTypes,
  highlightTypes,
  riskReliefSmartFunctionTypes,
} from '../../../../constants/constants'
import {
  useAlgorithmRunDetails,
  useSmartFunctionActionDropdownItems,
  useSmartFunctionActions,
  useWorkspaceDetails,
} from '../../../../hooks'
import { SmartFunctionActionDropdownKeys } from '../../../../hooks/useSmartFunctionActions'
import { notificationService } from '../../../../services'
import {
  AlgorithmRun,
  ConformityCheckQualityReviewItem,
  RiskReliefAlgorithmRun,
} from '../../../../store/reducers/workspaceReducer'

interface SmartFunctionActionDropdownProps {
  algorithmRun?: RiskReliefAlgorithmRun | AlgorithmRun
  disabled?: boolean
  deviations?: ConformityCheckQualityReviewItem[]
  showFlagDeviationsMenuItem?: boolean
  isHighlighted?: boolean
}

const SmartFunctionActionDropdown = ({
  algorithmRun,
  disabled,
  deviations,
  showFlagDeviationsMenuItem,
  isHighlighted,
}: SmartFunctionActionDropdownProps) => {
  const { workspaceId, algorithmRunId } = useParams()
  const [fetchDone, setFetchDone] = useState(false)

  const [selectedAlgorithmRunId, setSelectedAlgorithmRunId] = useState<
    string | undefined
  >(algorithmRunId)

  const { menuItems } = useSmartFunctionActionDropdownItems({
    workspaceId,
    algorithmRunId,
    algorithmRun,
    showFlagDeviationsMenuItem,
    deviations,
  })

  const { selectedWorkspace, refreshWorkspaceDetails } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { refreshAlgorithmRun, selectedAlgorithmRun } = useAlgorithmRunDetails({
    algorithmRunId: selectedAlgorithmRunId,
    workspaceId,
    preventFetch: true,
  })

  const {
    resumeSmartFunctionNode,
    handleAlgorithmRunClick,
    handleCancelRunClick,
    handleExportRun,
    handlePrintContent,
  } = useSmartFunctionActions({
    workspaceId,
    algorithmRun,
    algorithmRunId,
  })

  useEffect(() => {
    if (algorithmRunId && algorithmRunId !== selectedAlgorithmRunId) {
      setSelectedAlgorithmRunId(algorithmRunId)
      setFetchDone(false)
    }
  }, [algorithmRunId, selectedAlgorithmRunId])

  useEffect(() => {
    const fetchData = async () => {
      if (
        selectedAlgorithmRunId &&
        !fetchDone &&
        selectedAlgorithmRun?.id !== selectedAlgorithmRunId
      ) {
        await refreshWorkspaceDetails()
        await refreshAlgorithmRun()
        setFetchDone(true)
      }
    }
    fetchData()
  }, [
    selectedAlgorithmRun,
    fetchDone,
    refreshAlgorithmRun,
    refreshWorkspaceDetails,
    selectedAlgorithmRunId,
  ])

  const handleResume = async () => {
    if (!selectedWorkspace || !algorithmRun) return
    if (
      (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
      riskReliefSmartFunctionTypes.CAPA_GENERATION
    ) {
      await resumeSmartFunctionNode({
        workspaceId: workspaceId || '',
        runId: algorithmRun.id,
        nodeId: algorithmRunResumeNodeTypes.CAPA_GENERATION,
      })
    }
  }

  const handleRunClick = async () => {
    if (!selectedWorkspace || !algorithmRun) return
    if (
      (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
      riskReliefSmartFunctionTypes.CAPA_GENERATION
    ) {
      await handleResume()
    } else {
      await handleAlgorithmRunClick()
    }
  }

  const handleCancelRun = async () => {
    if (!selectedWorkspace || !algorithmRun) return
    try {
      await handleCancelRunClick()
      if (
        (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
        riskReliefSmartFunctionTypes.CAPA_GENERATION
      ) {
        notificationService.notificationError({
          messageToDisplay: t(
            'smartFunctionDetailsPage.capaGenerationRunCancelled'
          ),
          duration: NOTIFICATION_DEFAULT_DURATION,
          closable: true,
        })
      } else {
        notificationService.notificationError({
          messageToDisplay: t('smartFunctionDetailsPage.runCancelled'),
        })
      }
    } catch (error) {
      console.error('Error cancelling run:', error)
    }
  }

  const handlers = {
    [SmartFunctionActionDropdownKeys.RUN_ALGORITHM]: handleRunClick,
    [SmartFunctionActionDropdownKeys.RESUME_RUN]: handleRunClick,
    [SmartFunctionActionDropdownKeys.CANCEL_RUN]: handleCancelRun,
    [SmartFunctionActionDropdownKeys.EXPORT_RUN]: handleExportRun,
    [SmartFunctionActionDropdownKeys.PRINT_CONTENT]: handlePrintContent,
  }

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          const handlerKey = key as keyof typeof handlers
          handlers[handlerKey]?.()
        },
      }}
      placement="bottomRight"
      trigger={['hover']}
      disabled={disabled}
    >
      <HighlightActionButton
        icon={<MoreOutlined />}
        onClick={(e) => e.stopPropagation()}
        $highlight={
          isHighlighted ? highlightTypes.WARNING : highlightTypes.NONE
        }
      />
    </Dropdown>
  )
}

export default SmartFunctionActionDropdown
